/**
 * Marketing homepage (public landing).
 *
 * A lean landing: hero, trust strip, a condensed capability overview, and a
 * pricing teaser — each linking to the dedicated Features / Pricing pages.
 * Drives to /register (Get started) and /login (Sign in).
 *
 * @module pages/Home
 */

import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { MarketingLayout } from '../components/MarketingLayout';
import { PillarCard, CheckIcon, PRICE_FIRST, PRICE_ADDL, FinalCTA } from './marketing/shared';

export default function Home(): ReactElement {
  return (
    <MarketingLayout>
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-4 pt-16 pb-12 md:pt-24">
        <div className="grid items-center gap-12 md:grid-cols-2">
          <div>
            <p className="inline-flex rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700">
              Small-business payroll — one simple price
            </p>
            <h1 className="mt-4 text-4xl font-extrabold leading-tight tracking-tight text-gray-900 md:text-5xl">
              Payroll for small business.{' '}
              <span className="text-brand-700">Just payroll, done right.</span>
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-gray-600">
              Run payroll, file your federal and state taxes, and pay contractors — in one focused
              tool. No HR bloat, no surprises — one simple price.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/register"
                className="rounded-lg bg-brand-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-700"
              >
                Get started
              </Link>
              <Link
                to="/login"
                className="rounded-lg border border-gray-300 px-6 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
              >
                Sign in
              </Link>
            </div>
            <p className="mt-4 text-sm text-gray-500">
              From <span className="font-semibold text-gray-700">${PRICE_FIRST}/mo</span> for your first employee.
            </p>
          </div>

          {/* Hero product mockup */}
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <span className="text-sm font-semibold text-gray-800">Run payroll</span>
              <span className="rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700">Ready</span>
            </div>
            <div className="space-y-2.5 py-4">
              {[
                ['Jane Smith', 'Salary', '$5,000.00'],
                ['Robert Johnson', 'Hourly · 80h', '$2,000.00'],
                ['Maria Garcia', 'Contractor', '$1,500.00'],
              ].map(([name, type, amt]) => (
                <div key={name} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2.5">
                  <div>
                    <div className="text-sm font-medium text-gray-800">{name}</div>
                    <div className="text-xs text-gray-500">{type}</div>
                  </div>
                  <div className="text-sm font-semibold text-gray-900">{amt}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between border-t border-gray-100 pt-3">
              <span className="text-xs text-gray-500">Taxes &amp; filings handled</span>
              <span className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white">Submit payroll</span>
            </div>
          </div>
        </div>

        {/* Trust strip */}
        <div className="mt-14 grid grid-cols-2 gap-4 rounded-2xl border border-gray-100 bg-gray-50 p-6 text-center md:grid-cols-4">
          {[
            ['🔒', 'Bank-grade encryption', 'SSNs & bank data, AES-256'],
            ['🏛️', 'Federal + state taxes', 'Calculated & filed'],
            ['📄', 'All the forms', 'W-2, 1099-NEC, 941, 940 & state returns'],
            ['⚡', 'Payroll in minutes', 'Salary, hourly & tipped'],
          ].map(([icon, title, sub]) => (
            <div key={title}>
              <div className="text-2xl">{icon}</div>
              <div className="mt-1 text-sm font-semibold text-gray-800">{title}</div>
              <div className="text-xs text-gray-500">{sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Capability overview (condensed; links to /features) ───────────── */}
      <section className="mx-auto max-w-6xl px-4 py-16">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold text-gray-900">Everything you need to pay your team</h2>
          <p className="mt-3 text-gray-600">Three jobs, done well — without the platform bloat.</p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          <PillarCard title="Payroll" body="Salaried, hourly, and tipped pay with automatic overtime, federal withholding, and FICA. Unlimited pay runs." />
          <PillarCard title="Tax filing" body="Federal 941 & 940, your state unemployment report, and year-end W-2s — calculated and filed for you." />
          <PillarCard title="Contractors" body="Pay 1099 contractors alongside employees and generate 1099-NEC forms at year-end." />
        </div>
        <div className="mt-8 text-center">
          <Link to="/features" className="text-sm font-semibold text-brand-700 hover:text-brand-800">
            See all features →
          </Link>
        </div>
      </section>

      {/* ── Pricing teaser (links to /pricing) ────────────────────────────── */}
      <section className="bg-gray-50">
        <div className="mx-auto max-w-3xl px-4 py-16 text-center">
          <h2 className="text-3xl font-bold text-gray-900">One simple price</h2>
          <p className="mt-3 text-gray-600">Tax filing always included. No surprise fees.</p>
          <div className="mt-6 flex items-end justify-center gap-1">
            <span className="text-5xl font-extrabold text-gray-900">${PRICE_FIRST}</span>
            <span className="mb-1 text-gray-500">/mo first employee</span>
          </div>
          <p className="mt-1 text-sm font-medium text-gray-800">+ ${PRICE_ADDL}/mo per additional employee</p>
          <ul className="mx-auto mt-6 flex max-w-md flex-col gap-2 text-left sm:flex-row sm:flex-wrap sm:justify-center">
            {['Unlimited pay runs', 'All tax filings included', 'W-2, 1099, 941, 940 & state returns'].map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-gray-700 sm:w-auto">
                <CheckIcon /> {f}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/register" className="rounded-lg bg-brand-600 px-6 py-3 text-sm font-semibold text-white hover:bg-brand-700">
              Get started
            </Link>
            <Link to="/pricing" className="rounded-lg border border-gray-300 px-6 py-3 text-sm font-semibold text-gray-700 hover:bg-white">
              See pricing details
            </Link>
          </div>
        </div>
      </section>

      <FinalCTA />
    </MarketingLayout>
  );
}
